import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import qs from 'qs';
import { map } from 'rxjs/operators';
import { catchError } from 'rxjs/internal/operators/catchError';
import { throwError } from 'rxjs/internal/observable/throwError';
import { TranslateService } from '@ngx-translate/core';
import {
  CmsAdv,
  CmsApiError,
  CmsAsset,
  CmsCategory,
  CmsConfig,
  CmsConnection,
  CmsConnectionCategoryRelation,
  CmsHeaderIcon,
  CmsHighlight,
  CmsImageFormats,
  CmsQueryStringOptions,
  CmsResponse,
  CmsSrcSet,
  CmsStoreModelGroup,
  CmsStoreModelGroupRelation,
} from '../../_models/connect/cms';
import { environment } from '../../../../environments/environment';
import { ThemeConfig } from '../../../../assets/data/enum/themeConfig';

@Injectable()
export class CmsAuthInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Get the environment configuration
    let authReq = req.clone();

    // Check if the request URL includes the CMS service URL
    if (req.url.includes(environment.cmsServiceUrl)) {
      // Clone the request to add the authorization header
      authReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${environment.cmsApiKey}`),
      });
    }
    return next.handle(authReq);
  }
}

@Injectable({ providedIn: 'root' })
export class CmsService {
  private cmsServiceUrl = environment.cmsServiceUrl;
  private translate = inject(TranslateService);

  // Data accessible outside service
  $currentConfiguration: BehaviorSubject<CmsConfig> = new BehaviorSubject<CmsConfig>(null);
  $configurations: BehaviorSubject<CmsConfig[]> = new BehaviorSubject<CmsConfig[]>([]);
  $categories: BehaviorSubject<CmsCategory[]> = new BehaviorSubject<CmsCategory[]>([]);
  $headerIcons: BehaviorSubject<CmsHeaderIcon[]> = new BehaviorSubject<CmsHeaderIcon[]>([]);
  $highlights: BehaviorSubject<CmsHighlight[]> = new BehaviorSubject<CmsHighlight[]>([]);
  $connections: BehaviorSubject<CmsConnection[]> = new BehaviorSubject<CmsConnection[]>([]);
  $selectedConnection: BehaviorSubject<CmsConnection> = new BehaviorSubject<CmsConnection>(null);
  $connectionCategoryRelations: BehaviorSubject<CmsConnectionCategoryRelation[]> =
    new BehaviorSubject<CmsConnectionCategoryRelation[]>([]);
  $selectedCategory: BehaviorSubject<CmsCategory> = new BehaviorSubject<CmsCategory>(null);
  $filteredCategories: BehaviorSubject<CmsCategory[]> = new BehaviorSubject<CmsCategory[]>([]);
  $searchQuery: BehaviorSubject<string> = new BehaviorSubject<string>('');
  $advs: BehaviorSubject<CmsAdv[]> = new BehaviorSubject<CmsAdv[]>([]);

  constructor(private http: HttpClient) {}

  /**SERVICES**/
  /**
   * Get the CMS initial configurations
   * @param options Object containing populate, fields, and filters properties
   * @returns {Observable<CmsConfigResponse>}
   */
  getCmsConfigurations(options: CmsQueryStringOptions): Observable<CmsResponse<CmsConfig[]>> {
    const queryString = this.buildStrapiQueryString(options);
    return this.http.get(`${this.cmsServiceUrl}/api/configs?${queryString}`).pipe(
      map((response: CmsResponse<CmsConfig[]>) => {
        this.$configurations.next(response.data);

        // Set the config related to the current SaaS
        const currentConfig = response.data.find((config) => {
          // Check if there is a saasConfig matching the condition
          return !!config.saasConfigList.find(
            (saasConfig) => saasConfig.code === ThemeConfig.brand.toUpperCase()
          );
        });
        this.$currentConfiguration.next(currentConfig);
        return response;
      }),
      catchError((error: CmsApiError) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Get the CMS relations between connections and categories
   * @param options
   * @returns {Observable<CmsConnectionsResponse>}
   */
  getConnectionsCategoriesRelations(
    options: CmsQueryStringOptions
  ): Observable<CmsResponse<CmsConnectionCategoryRelation[]>> {
    const queryString = this.buildStrapiQueryString(options);
    return this.http
      .get(`${this.cmsServiceUrl}/api/connection-category-relations?${queryString}`)
      .pipe(
        map((response: CmsResponse<CmsConnectionCategoryRelation[]>) => {
          this.$connectionCategoryRelations.next(response.data);
          return response;
        }),
        catchError((error: CmsApiError) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Get the CMS relations between connections and categories
   * @param options
   * @returns {Observable<CmsConnectionsResponse>}
   */
  getStoreModelGroupsRelations(
    options: CmsQueryStringOptions
  ): Observable<CmsResponse<CmsStoreModelGroupRelation[]>> {
    const queryString = this.buildStrapiQueryString(options);
    return this.http
      .get(`${this.cmsServiceUrl}/api/store-model-group-relations?${queryString}`)
      .pipe(
        map((response: CmsResponse<CmsStoreModelGroupRelation[]>) => {
          return response;
        }),

        catchError((error: CmsApiError) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Gets the CMS categories and populates them with connections
   * @param options Object containing populate, fields, and filters properties
   * @returns {Observable<CmsCategoryResponse>}
   */
  getCategories(options: CmsQueryStringOptions): Observable<CmsResponse<CmsCategory[]>> {
    const queryString = this.buildStrapiQueryString(options);
    return this.http.get(`${this.cmsServiceUrl}/api/categories?${queryString}`).pipe(
      map((response: CmsResponse<CmsCategory[]>) => {
        const result = response.data;

        const relations = this.$connectionCategoryRelations.getValue();
        const currentConfig = this.$currentConfiguration.getValue();

        // Adding the connections for each category not duplicating
        result.forEach((category) => {
          const relationsToFill = relations.filter(
            (rel) => rel.category.id === category.id && rel.config.id === currentConfig.id
          );

          if (!category.connections) {
            category['connections'] = [];
          }

          relationsToFill.forEach((rel) => {
            // IF connection is not already in the category connections array
            category.connections.push(rel.connection);
          });
        });

        this.$categories.next(result);

        // Update the categories
        this.updateSelectedCategory(null);
        return response;
      }),
      catchError((error: CmsApiError) => {
        return throwError(() => error);
      })
    );
  }

  getConnections(options: CmsQueryStringOptions): Observable<CmsResponse<CmsConnection[]>> {
    const queryString = this.buildStrapiQueryString(options);
    return this.http.get(`${this.cmsServiceUrl}/api/connections?${queryString}`).pipe(
      map((response: CmsResponse<CmsConnection[]>) => {
        this.$connections.next(response.data);
        return response;
      }),
      catchError((error: CmsApiError) => {
        return throwError(() => error);
      })
    );
  }

  getConnection(
    options: CmsQueryStringOptions,
    id: string
  ): Observable<CmsResponse<CmsConnection>> {
    const queryString = this.buildStrapiQueryString(options);
    return this.http.get(`${this.cmsServiceUrl}/api/connections/${id}?${queryString}`).pipe(
      map((response: CmsResponse<CmsConnection>) => {
        this.$selectedConnection.next(response.data);
        return response;
      }),
      catchError((error: CmsApiError) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Get the CMS highlights
   * @param options
   * @returns {Observable<CmsHighlightResponse>}
   */
  getHighlights(options: CmsQueryStringOptions): Observable<CmsResponse<CmsHighlight[]>> {
    const queryString = this.buildStrapiQueryString(options);
    return this.http.get(`${this.cmsServiceUrl}/api/highlights?${queryString}`).pipe(
      map((response: CmsResponse<CmsHighlight[]>) => {
        this.$highlights.next(response.data);
        return response;
      }),
      catchError((error: CmsApiError) => {
        return throwError(() => error);
      })
    );
  }

  getHeaderIcons(options: CmsQueryStringOptions): Observable<CmsResponse<CmsHeaderIcon[]>> {
    const queryString = this.buildStrapiQueryString(options);
    return this.http.get(`${this.cmsServiceUrl}/api/header-icons?${queryString}`).pipe(
      map((response: CmsResponse<CmsHeaderIcon[]>) => {
        this.$headerIcons.next(response.data);
        return response;
      }),
      catchError((error: CmsApiError) => {
        return throwError(() => error);
      })
    );
  }

  getAdvs(options: CmsQueryStringOptions): Observable<CmsResponse<CmsAdv[]>> {
    const queryString = this.buildStrapiQueryString(options);
    return this.http.get(`${this.cmsServiceUrl}/api/advs?${queryString}`).pipe(
      map((response: CmsResponse<CmsAdv[]>) => {
        this.$advs.next(response.data);
        return response;
      }),
      catchError((error: CmsApiError) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Get the store model groups
   * @param options Object containing populate, fields, and filters properties
   * @returns {Observable<CmsResponse<CmsStoreModelGroup[]>>}
   * This method retrieves store model groups from the CMS service.
   */
  getStoreModelGroups(
    options?: CmsQueryStringOptions
  ): Observable<CmsResponse<CmsStoreModelGroup[]>> {
    const queryString = this.buildStrapiQueryString(options);
    return this.http.get(`${this.cmsServiceUrl}/api/store-model-groups?${queryString}`).pipe(
      map((response: CmsResponse<any[]>) => {
        return response;
      }),
      catchError((error: CmsApiError) => {
        return throwError(() => error);
      })
    );
  }

  getAssets(options: CmsQueryStringOptions): Observable<CmsResponse<CmsAsset[]>> {
    const queryString = this.buildStrapiQueryString(options);
    return this.http.get(`${this.cmsServiceUrl}/api/assets?${queryString}`).pipe(
      map((response: CmsResponse<CmsAsset[]>) => {
        return response;
      }),
      catchError((error: CmsApiError) => {
        return throwError(() => error);
      })
    );
  }

  /**METHODS**/

  updateSearchQuery(query: string) {
    console.log('updating search query', query);
    this.$searchQuery.next(query);
    this.joinCategoriesAndConnections();
  }

  updateSelectedCategory(category: CmsCategory) {
    this.$selectedCategory.next(category);
    this.joinCategoriesAndConnections();
  }

  joinCategoriesAndConnections(): CmsCategory[] {
    const categories = JSON.parse(JSON.stringify(this.$categories.getValue()));

    // setting result to empty array
    let result: CmsCategory[] = [];

    // getting selected category
    const selectedCategory = this.$selectedCategory.getValue();

    // setting filtered categories from selected category
    // if selected category is not null set the result
    // to the categories that have the selected category as parent
    if (selectedCategory) {
      result = categories.filter((category) => {
        return (
          (category.category && category.category.documentId === selectedCategory.documentId) ||
          category.documentId === selectedCategory.documentId
        );
      });
    } else {
      // if selected category is null set the result to the categories that have no parent
      result = categories.filter((category) => !category.category);
    }

    // filtering out categories that have no connections remaining after filtering
    result = result.filter((category) => category.connections && category.connections.length > 0);
    this.$filteredCategories.next(result);
    return result;
  }

  /** UTILITIES**/

  /**
   * Builds a query string for Strapi API requests based on options using the qs library
   * @param options Object containing populate, fields, and filters properties
   * @returns A formatted query string for Strapi requests
   */
  buildStrapiQueryString(options: CmsQueryStringOptions): string {
    // replace '_' with '-' in current language code
    let { currentLang } = this.translate;
    if (currentLang.includes('_')) {
      currentLang = currentLang.replace('_', '-');
    }

    const queryObject: any = {
      locale: currentLang,
      populate: options.populate,
      fields: options.fields,
      filters: options.filters,
      pagination: options.pagination,
      sort: options.sort,
      status: 'published',
    };

    return qs.stringify(queryObject, { encodeValuesOnly: true });
  }

  /**
   * Get the srcset attribute for an image starting from the "formats" Strapi object
   * @param formats The formats object from Strapi
   * @returns {CmsSrcSet} The srcset attributes size and srcSet
   */
  getImageSrcSet(formats: CmsImageFormats): CmsSrcSet {
    const result = {
      srcSet: '',
      sizes: '',
    };

    // cycle trough the formats object and build the srcset attribute
    // On strapi the formats object is an object with the format name as key
    // and the format object as value we have to buld the srcSet attributes
    for (const key in formats) {
      if (formats.hasOwnProperty(key)) {
        const format = formats[key];
        result.srcSet += `${format.url} ${format.width}w,`;
        result.sizes += `(max-width: ${format.width}px) ${format.width}px,`;
      }
    }

    return result;
  }

  /**
   * Adds in localstorage an item to the recent connections searches removing the last if we get to 10
   * @param connection
   * @type {CmsConnection[]}
   * @returns {void}
   */

  addRecentConnectionSearch(connection: CmsConnection) {
    // Get the recent connections from localstorage or an empty array
    let recentConnections: CmsConnection[] =
      JSON.parse(localStorage.getItem('recentConnections')) || [];

    // Remove the connection if already present and add it to the beginning of the array
    recentConnections = recentConnections.filter(
      (c) => c.documentId && c.documentId !== connection.documentId
    );
    recentConnections.unshift(connection);

    // Remove the last connection if we have more than 10
    if (recentConnections.length > 10) {
      recentConnections.pop();
    }

    // Save the recent connections to localstorage
    localStorage.setItem('recentConnections', JSON.stringify(recentConnections));
  }

  getRecentConnectionSearches(): CmsConnection[] {
    return JSON.parse(localStorage.getItem('recentConnections')) || [];
  }
}
